import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabase';

export const usePushNotifications = () => {
  const { user } = useAuth();
  const [pushSupported, setPushSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check push support and existing subscription on mount
  useEffect(() => {
    const checkPushSupport = async () => {
      try {
        if ('serviceWorker' in navigator && 'PushManager' in window) {
          setPushSupported(true);
          await checkSubscription();
        } else {
          setPushSupported(false);
        }
      } catch (err) {
        console.error('Push support check failed:', err);
      } finally {
        setLoading(false);
      }
    };

    checkPushSupport();
  }, []);

  const checkSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setIsSubscribed(!!subscription);
    } catch (err) {
      console.error('Subscription check failed:', err);
      setIsSubscribed(false);
    }
  };

  const subscribeToPush = async (): Promise<boolean> => {
    if (!pushSupported || !user) {
      setError('Push notifications not supported');
      return false;
    }

    try {
      setError(null);
      const registration = await navigator.serviceWorker.ready;

      // Get VAPID public key from environment
      const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
      if (!vapidPublicKey) {
        setError('Push configuration missing');
        return false;
      }

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) as BufferSource,
      });

      // Save subscription to database
      await savePushSubscription(subscription);
      setIsSubscribed(true);
      return true;
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : 'Failed to subscribe';
      setError(errMsg);
      console.error('Push subscription failed:', err);
      return false;
    }
  };

  const unsubscribeFromPush = async (): Promise<boolean> => {
    if (!pushSupported) return false;

    try {
      setError(null);
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe();
        await clearPushSubscription();
        setIsSubscribed(false);
        return true;
      }
      return false;
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : 'Failed to unsubscribe';
      setError(errMsg);
      console.error('Push unsubscription failed:', err);
      return false;
    }
  };

  const savePushSubscription = async (subscription: PushSubscription) => {
    if (!user) return;

    try {
      const { p256dh, auth } = subscription.getKey('p256dh') && subscription.getKey('auth')
        ? {
            p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')!))),
            auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth')!))),
          }
        : { p256dh: null, auth: null };

      const { error } = await supabase
        .from('notification_preferences')
        .upsert({
          user_id: user.id,
          push_subscription_endpoint: subscription.endpoint,
          push_subscription_p256dh: p256dh,
          push_subscription_auth: auth,
        })
        .eq('user_id', user.id);

      if (error) {
        throw error;
      }
    } catch (err) {
      console.error('Failed to save push subscription:', err);
      throw err;
    }
  };

  const clearPushSubscription = async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('notification_preferences')
        .update({
          push_subscription_endpoint: null,
          push_subscription_p256dh: null,
          push_subscription_auth: null,
        })
        .eq('user_id', user.id);

      if (error) {
        throw error;
      }
    } catch (err) {
      console.error('Failed to clear push subscription:', err);
      throw err;
    }
  };

  return {
    pushSupported,
    isSubscribed,
    loading,
    error,
    subscribeToPush,
    unsubscribeFromPush,
  };
};

/**
 * Convert VAPID key from base64 URL-safe to Uint8Array
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}
